import { ICoreContext } from '@msdyn365-commerce/core';
import { AttributeValueBase } from '@msdyn365-commerce/retail-proxy';
import React from 'react';
import {
    IAddress,
    IAllDocuments,
    IAnticapteIssues,
    IAppalentInformation,
    IAppealAgainst,
    IBringingWitness,
    ICustomerInformation,
    IDateAndReasonForAppealReadOnly,
    IDocumentAvailibility,
    ILCSABAgentInformaitonReadOnly,
    ILCSABAppellantInformationReadOnly,
    ILCSABCustomerInformationReadOnly,
    ILegalDescription,
    ILegalDescriptionCondo,
    ILineDocument
} from './types/order';
import { IGroup, IGroups, ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { PropertyType } from './propertyTypeEnum';
import { getDocumentBlobSecureUrlAsync } from '../../../customProxy/retailExtension/DataActionExtension.g';
import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

export const _downloadDocument = async (context: ICoreContext, fileName: string, secureURL: string | undefined) => {
    try {
        const link = document.createElement('a');
        link.href = secureURL || '';
        // TODO: Need to get file Extension from API response in future.
        link.setAttribute('download', `${fileName}`);
        link.setAttribute('target', `_blank`);
        // Append to html link element page
        document.body.appendChild(link);
        // Start download
        link.click();
        // Clean up and remove the link
        link.parentNode && link.parentNode.removeChild(link);
        return;
    } catch (error) {
        context.telemetry.error(`Failed to download the file "${fileName}" ${error}`);
        return null;
    }
};

/* This method is use to render download button for document download */
export const renderDownloadDocumentButton = (
    lineAttributes: AttributeValueBase[] | undefined,
    customContext: ICoreContext | undefined,
    productName: string,
    downloadLabel: string,
    lineDocument: ILineDocument[],
    fetchTimeoutLabel?: string,
    fetchRetryLabel?: string,
    processingLabel?: string,
    retry?: () => void,
    noOfRetries?: number,
    maxRetries?: number,
): React.ReactNode => {
    const { searchKeyNameAttribute, searchKeyValueAttribute, documentAttribute } = customContext?.request.app.config;

    const createDownloadButton = (matchedDocument: ILineDocument, documentName: string): React.ReactNode => {
        return matchedDocument.isError  ? (
            <button className={`App-link ms-order-confirmation__btn-download`} disabled={noOfRetries! >= (maxRetries! ?? 2)} onClick={retry}>
                {noOfRetries! < (maxRetries! ?? 2) ? fetchRetryLabel : fetchTimeoutLabel}
            </button>
        ) : (
            <button
                disabled={!matchedDocument?.isSet}
                className={`App-link ms-order-confirmation__btn-download ${!matchedDocument.isSet && 'procession-btn'}`}
                onClick={() => _downloadDocument(customContext!, `${documentName}`, matchedDocument?.secureURL)}
            >
                {!matchedDocument.isSet ? (
                    <>
                        <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>{' '}
                        <span>{processingLabel}</span>
                    </>
                ) : (
                    downloadLabel
                )}
            </button>
        );
    };

    if (lineAttributes) {
        const attributeName = lineAttributes.find(line => line.Name === searchKeyNameAttribute);
        const attributeValue = lineAttributes.find(line => line.Name === searchKeyValueAttribute);
        // @ts-ignore
        const atributeNameText = attributeName?.TextValue?.trim();
        const linePropertyType = atributeNameText ? +atributeNameText : PropertyType.default;
        // @ts-ignore
        const lineValue = attributeValue?.TextValue!;

        switch (linePropertyType) {
            case PropertyType.address:
            case PropertyType.legalDescription:
            case PropertyType.legalDescriptionCondo:
                {
                    const addressValue = createName(createName(lineValue, '||'), ' ');
                    const documentName = createDownloadDocName(createName(productName, ' '), addressValue);
                    const matchedDocument = lineDocument.find(line => line.documentName === documentName);
                    if (matchedDocument) {
                        return createDownloadButton(matchedDocument, documentName);
                    }
                }
                break;
            case PropertyType.sdab:
            case PropertyType.lcsab:
                return;
            // below all cases have same implementation
            case PropertyType.quadrant:
            case PropertyType.blockProfile:
            case PropertyType.ats:
            case PropertyType.cityWide:
            case PropertyType.none:
            case PropertyType.community:
                {
                    const attributeDocument = lineAttributes.find(line => line.Name === documentAttribute);
                    // @ts-ignore
                    const lineDocumentValue: string = attributeDocument?.TextValue! || '';
                    const documentName = lineDocumentValue
                        .split('||')[1]
                        .trim()
                        .toLowerCase();
                    const matchedDocument = lineDocument.find(line => line.documentName.toLowerCase() === documentName);
                    if (matchedDocument) {
                        return createDownloadButton(matchedDocument, documentName);
                    }
                }
                break;
            case PropertyType.cpid:
            case PropertyType.rollNumber:
            case PropertyType.lincNumber:
            case PropertyType.titleNumber:
                {
                    const documentName = createDownloadDocName(createName(productName, ' '), lineValue?.trim());
                    const matchedDocument = lineDocument.find(line => line.documentName === documentName);
                    if (matchedDocument) {
                        return createDownloadButton(matchedDocument, documentName);
                    }
                }
                break;
            default:
                return;
        }
    }
    return;
};
// function is to keep requesting after specific interval of time to Azure blob to check if doc is available or not.
export function checkDocumentAvailibility(docs: IDocumentAvailibility) {
    const { groups, lineDocument, setLineDocument, allDocuments, context, channelReferenceId } = docs;
    // getting app level configs
    const config = context?.request.app.config;
    const interval = setInterval(() => {
        if (allDocuments.isAvailable) {
            groups?.groups.forEach((group: IGroup) => {
                group.salesLines.forEach((salesLine: ISalesLine, index: number) => {
                    const { searchKeyNameAttribute } = config;
                    const attributeName =
                        salesLine.data.salesLine.AttributeValues &&
                        salesLine.data.salesLine.AttributeValues.find(line => line.Name === searchKeyNameAttribute);
                    // @ts-ignore
                    const linePropertyType: number = parseInt(attributeName?.TextValue!);
                    let name: string = '';
                    name = createDocumentName(salesLine, config);
                    if (name != '') {
                        if (
                            linePropertyType != PropertyType.community &&
                            linePropertyType != PropertyType.blockProfile &&
                            linePropertyType != PropertyType.quadrant &&
                            linePropertyType != PropertyType.ats &&
                            linePropertyType != PropertyType.none &&
                            linePropertyType != PropertyType.cityWide
                        ) {
                            name = createDownloadDocName(createName(salesLine.data.product?.Name!, ' '), name);
                        }

                        // getting line from the state whoes doc is still not received
                        const stateLine = lineDocument.find(line => line.documentName === name && line.isSet === false);
                        if (stateLine?.documentName === name && stateLine.isSet === false) {
                            let nameWithExtension = name;
                            if (
                                linePropertyType != PropertyType.community &&
                                linePropertyType != PropertyType.blockProfile &&
                                linePropertyType != PropertyType.quadrant &&
                                linePropertyType != PropertyType.ats &&
                                linePropertyType != PropertyType.none &&
                                linePropertyType != PropertyType.cityWide
                            ) {
                                nameWithExtension = `${nameWithExtension}${stateLine.documentType}`;
                            }
                            const uniqueId = new Date().toString();
                            getDocumentBlobSecureUrlAsync(
                                { callerContext: context.actionContext },
                                channelReferenceId || '',
                                nameWithExtension,
                                uniqueId
                            )
                                .then(response => {
                                    if (response.Status) {
                                        const docIndex = lineDocument.indexOf(lineDocument.find(line => line.documentName === name)!);
                                        lineDocument[docIndex].secureURL = response.SecureBlobUrl;
                                        lineDocument[docIndex].isSet = true;
                                        
                                        setLineDocument([...lineDocument]);
                                    }
                                })
                                .catch(error => {
                                    lineDocument.find(line => line.documentName === name)!.isError = true;
                                   
                                    setLineDocument([...lineDocument]);
                                    context.telemetry.error(`Failed to download the file ${error}`);
                                });
                        }
                    }
                });
            });
        }
    }, config.requestInterval);

    // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
    return interval;
}
export function createName(name: string, strDelimeter: string) {
    return name
        .trim()
        .split(strDelimeter)
        .map((val: string) => val?.trim())
        .filter(val => val != '')
        .join('_')
        .trim();
}
export function createDownloadDocName(productName: string, searchKey: string) {
    return `${searchKey.toUpperCase()}_${productName}`;
}
// function to set initial state of lines
export async function setLineDocumentState(
    groups: IGroups | undefined,
    context: ICoreContext<{
        [x: string]: any;
    }>,
    lineDocument: ILineDocument[],
    setLineDocument: React.Dispatch<React.SetStateAction<ILineDocument[]>>
) {
    const {
        searchKeyNameAttribute,
        searchKeyValueAttribute,
        azureBlobFileFormat,
        documentAttribute,
        folioNumberValueAttribute,
        productTypePA,
        colCDLICProductID
    } = context.app.config;
    const {
        customerFirstName,
        customerLastName,
        customerEmail,
        customerEmailConfirm,
        productOwnerEmail,
        customerAddress,
        customerAddress2,
        customerCity,
        customerProvince,
        customerPostalCode,
        customerResidentialPhone,
        customerCountry,
        siteMunicipalAddressOfSite,
        siteOtherDocuments,
        appelantFirstName,
        appelantLastName,
        appelantAgentName,
        appelantEmailAddress,
        appelantEmailConfirmAddress,
        appelantAddress,
        appelantAddress2,
        appelantCity,
        appelantProvince,
        appelantPostalCode,
        appelantResidentialPhone,
        appealDevelopmentPermitType,
        appealReasonForAppeal,
        appealEstimatedPresentationTime,
        appealUsingAgent,
        appealAnticipateIssues,
        appealReason,
        appealBringingWitness,
        appealNumberOfWitness,
        customerInfoDPN,
        customerInfoSPN,
        appellantInfoDPN,
        appellantInfoSPN,
        usingAgentInfo,
        lcsabAgentName,
        lcsabAgentEmailAddress,
        lcsabAgentAddress1,
        lcsabAgentAddress2,
        lcsabAgentCity,
        lcsabAgentProvince,
        lcsabAgentPostalCode,
        lcsabAgentDPN,
        lcsabAgentSPN,
        lcsabAgentDecisionDate,
        lcsabAppealType
    } = context.app.config;
    let lineDocs: ILineDocument[] = [];
    for (let gourpIndex = 0; gourpIndex < groups?.groups.length! || 0; gourpIndex++) {
        const group = groups?.groups[gourpIndex];
        for (let saleLineIndex = 0; saleLineIndex < group?.salesLines.length! || 0; saleLineIndex++) {
            const salesLine = group?.salesLines[saleLineIndex];
            if (
                salesLine?.data.salesLine.AttributeValues &&
                !lineDocument.some(line => line.lineNumber === salesLine?.data.salesLine.LineNumber)
            ) {
                const attributeName = salesLine.data.salesLine.AttributeValues.find(line => line.Name === searchKeyNameAttribute);
                const attributeValue = salesLine.data.salesLine.AttributeValues.find(line => line.Name === searchKeyValueAttribute);
                const attributeDocument = salesLine.data.salesLine.AttributeValues.find(line => line.Name === documentAttribute);
                const attributeFolioNumber = salesLine.data.salesLine.AttributeValues.find(line => line.Name === folioNumberValueAttribute);
                // @ts-ignore
                const atributeNameText = attributeName?.TextValue?.trim();
                const linePropertyType = atributeNameText ? +atributeNameText : PropertyType.default;
                // @ts-ignore
                const lineValue = attributeValue?.TextValue! || '';
                // @ts-ignore
                const lineDocumentValue: string = attributeDocument?.TextValue! || '';
                // @ts-ignore
                const lineFolioNumber: string = attributeFolioNumber?.TextValue! || '';

                let lineDoc: ILineDocument = { documentName: '', lineNumber: 0, isSet: false };
                
                const productName = salesLine.data.product?.Name;
                const productDetails = salesLine.data.product;

                // getting product attributes
                const attributeValues = await getAttributeValuesAsync(
                    { callerContext: context.actionContext, queryResultSettings: {} },
                    productDetails?.RecordId!,
                    context.request.apiSettings.channelId,
                    0
                );
                const prodType = attributeValues.find(attr => attr.Name === productTypePA)?.TextValue || '';
                const rateId = parseInt(attributeValues.find(attr => attr.Name === colCDLICProductID)?.TextValue!) || 0;
                const productOwnerEmailValue = attributeValues.find(attr => attr.Name === productOwnerEmail)?.TextValue || '';

                // we are sending address with all orders save address from server
                // default is for Roll number, LINC, CPID and Title Number as it is only a string array
                switch (linePropertyType) {
                    case PropertyType.address:
                        {
                            const addressArr = lineValue.split('||');
                            let lineSuite, lineNumber, lineStreetName, lineStreetType, lineQuadrant;
                            // check if address length is 5, it means address contain suite. if length is 4 it doesnot have suite
                            if (addressArr.length === 5) {
                                [lineSuite, lineNumber, lineStreetName, lineStreetType, lineQuadrant] = lineValue
                                    .split('||')
                                    .map((val: string) => val?.trim().toLowerCase());
                            } else if (addressArr.length === 4) {
                                [lineNumber, lineStreetName, lineStreetType, lineQuadrant] = lineValue
                                    .split('||')
                                    .map((val: string) => val?.trim().toLowerCase());
                            }
                            const address: IAddress = {
                                suite: lineSuite,
                                number: lineNumber,
                                streetName: lineStreetName,
                                streetType: lineStreetType,
                                quadrant: lineQuadrant
                            };
                            lineDoc = {
                                documentName: `${createDownloadDocName(
                                    createName(productName!, ' '),
                                    createName(createName(lineValue, '||'), ' ')
                                )}`,
                                lineNumber: salesLine.data.salesLine.LineNumber!,
                                isSet: false,
                                documentType: azureBlobFileFormat,
                                productType: prodType,
                                propertyType: linePropertyType,
                                address: { ...address },
                                productName: productName,
                                folioNumber: lineFolioNumber,
                                rateId: rateId,
                                productID: salesLine.data.product?.RecordId?.toString()
                            };
                        }
                        break;

                    case PropertyType.legalDescription:
                        {
                            let plan, block, lot;
                            [plan, block, lot] = lineValue.split('||').map((val: string) => val?.trim().toLowerCase());
                            let docName = '';
                            let legalDesc: ILegalDescription = {
                                plan: plan
                            };
                            docName = plan;
                            // check if block and lot is valid and have some value in it
                            if (block && block != '') {
                                legalDesc.block = block;
                                docName = `${docName}_${block}`;
                            }
                            if (lot && lot != '') {
                                legalDesc.lot = lot;
                                docName = `${docName}_${lot}`;
                            }
                            const productName = salesLine.data.product?.Name;
                            lineDoc = {
                                documentName: `${createDownloadDocName(createName(productName!, ' '), docName.trim())}`,
                                lineNumber: salesLine.data.salesLine.LineNumber!,
                                isSet: false,
                                documentType: azureBlobFileFormat,
                                propertyType: linePropertyType,
                                productType: prodType,
                                legalDesc: { ...legalDesc },
                                productName: productName,
                                folioNumber: lineFolioNumber,
                                rateId: rateId,
                                productID: salesLine.data.product?.RecordId?.toString()
                            };
                        }
                        break;

                    case PropertyType.legalDescriptionCondo:
                        {
                            const addressArr = lineValue.split('||');
                            let plan, unit;
                            // check if address length is 2, it means address contain unit. if length is 1 it doesnot have unit
                            if (addressArr.length === 2) {
                                [plan, unit] = lineValue.split('||').map((val: string) => val?.trim().toLowerCase());
                            } else {
                                [plan] = lineValue.trim().toLowerCase();
                            }
                            const legalDescCondo: ILegalDescriptionCondo = {
                                plan: plan,
                                unit: unit
                            };
                            const productName = salesLine.data.product?.Name;
                            lineDoc = {
                                documentName: `${createDownloadDocName(createName(productName!, ' '), createName(lineValue, '||'))}`,
                                lineNumber: salesLine.data.salesLine.LineNumber!,
                                productID: salesLine.data.product?.RecordId?.toString(),
                                isSet: false,
                                documentType: azureBlobFileFormat,
                                propertyType: linePropertyType,
                                productType: prodType,
                                legalDescCondo: { ...legalDescCondo },
                                productName: productName,
                                folioNumber: lineFolioNumber,
                                rateId: rateId
                            };
                        }
                        break;
                    case PropertyType.lcsab:
                        const attributes = [...salesLine.data.salesLine.AttributeValues];
                        const lcsabCustomerInfo: ILCSABCustomerInformationReadOnly = {
                            //@ts-ignore
                            firstName: attributes.find(line => line.Name === customerFirstName)?.TextValue!,
                            //@ts-ignore
                            lastName: attributes.find(line => line.Name === customerLastName)?.TextValue!,
                            //@ts-ignore
                            email: attributes.find(line => line.Name === customerEmail)?.TextValue!,

                            productOwnerEmail: productOwnerEmailValue,
                        
                            //@ts-ignore    
                            emailConfirm: attributes.find(line => line.Name === customerEmailConfirm)?.TextValue!,
                            //@ts-ignore
                            address1: attributes.find(line => line.Name === customerAddress)?.TextValue!,
                            //@ts-ignore
                            address2: attributes.find(line => line.Name === customerAddress2)?.TextValue!,
                            //@ts-ignore
                            city: attributes.find(line => line.Name === customerCity)?.TextValue!,
                            //@ts-ignore
                            province: attributes.find(line => line.Name === customerProvince)?.TextValue!,
                            //@ts-ignore
                            postalCode: attributes.find(line => line.Name === customerPostalCode)?.TextValue!,
                            //@ts-ignore
                            dayTimePhoneNumber: attributes.find(line => line.Name === customerInfoDPN)?.TextValue!,
                            //@ts-ignore
                            secondaryPhoneNumber: attributes.find(line => line.Name === customerInfoSPN)?.TextValue!
                        };
                        const lcsabAppellantInfo: ILCSABAppellantInformationReadOnly = {
                            //@ts-ignore
                            firstName: attributes.find(line => line.Name === appelantFirstName)?.TextValue!,
                            //@ts-ignore
                            lastName: attributes.find(line => line.Name === appelantLastName)?.TextValue!,
                            //@ts-ignore
                            email: attributes.find(line => line.Name === appelantEmailAddress)?.TextValue!,
                            productOwnerEmail: productOwnerEmailValue,
                            //@ts-ignore
                            emailConfirm: attributes.find(line => line.Name === appelantEmailConfirmAddress)?.TextValue!,
                            //@ts-ignore
                            address1: attributes.find(line => line.Name === appelantAddress)?.TextValue!,
                            //@ts-ignore
                            address2: attributes.find(line => line.Name === appelantAddress2)?.TextValue!,
                            //@ts-ignore
                            city: attributes.find(line => line.Name === appelantCity)?.TextValue!,
                            //@ts-ignore
                            province: attributes.find(line => line.Name === appelantProvince)?.TextValue!,
                            //@ts-ignore
                            postalCode: attributes.find(line => line.Name === appelantPostalCode)?.TextValue!,
                            //@ts-ignore
                            dayTimePhoneNumber: attributes.find(line => line.Name === appellantInfoDPN)?.TextValue!,
                            //@ts-ignore
                            secondaryPhoneNumber: attributes.find(line => line.Name === appellantInfoSPN)?.TextValue!,
                            //@ts-ignore
                            municipalAddress: attributes.find(line => line.Name === siteMunicipalAddressOfSite)?.TextValue!
                        };
                        //@ts-ignore
                        const usingAgentInformation: string = attributes.find(line => line.Name === usingAgentInfo)?.TextValue! as String;
                        const lcsabAgentInformation: ILCSABAgentInformaitonReadOnly = {
                            // Agent doeesn't have first and last name
                            firstName: '',
                            lastName: '',
                            //@ts-ignore
                            agentName: attributes.find(line => line.Name === lcsabAgentName)?.TextValue!,
                            //@ts-ignore
                            email: attributes.find(line => line.Name === lcsabAgentEmailAddress)?.TextValue!,
                            productOwnerEmail: productOwnerEmailValue,
                            //@ts-ignore
                            emailConfirm: attributes.find(line => line.Name === lcsabAgentEmailAddress)?.TextValue!,
                            //@ts-ignore
                            address1: attributes.find(line => line.Name === lcsabAgentAddress1)?.TextValue!,
                            //@ts-ignore
                            address2: attributes.find(line => line.Name === lcsabAgentAddress2)?.TextValue!,
                            //@ts-ignore
                            city: attributes.find(line => line.Name === lcsabAgentCity)?.TextValue!,
                            //@ts-ignore
                            province: attributes.find(line => line.Name === lcsabAgentProvince)?.TextValue!,
                            //@ts-ignore
                            postalCode: attributes.find(line => line.Name === lcsabAgentPostalCode)?.TextValue!,
                            //@ts-ignore
                            dayTimePhoneNumber: attributes.find(line => line.Name === lcsabAgentDPN)?.TextValue!,
                            //@ts-ignore
                            secondaryPhoneNumber: attributes.find(line => line.Name === lcsabAgentSPN)?.TextValue!
                        };
                        const lcsabDateAndReasonForAppeal: IDateAndReasonForAppealReadOnly = {
                            //@ts-ignore
                            date: attributes.find(line => line.Name === lcsabAgentDecisionDate)?.TextValue!,
                            //@ts-ignore
                            reasonForAppeal: attributes.find(line => line.Name === appealReasonForAppeal)?.TextValue!
                        };
                        //@ts-ignore
                        const categoryAppealType = attributes.find(line => line.Name === lcsabAppealType)?.TextValue! as string;
                        lineDoc = {
                            lcsab: {
                                customerInformation: lcsabCustomerInfo,
                                appellantInformation: lcsabAppellantInfo,
                                agentInformation: lcsabAgentInformation,
                                usingAgent: usingAgentInformation,
                                dateAndReasonForAppeal: lcsabDateAndReasonForAppeal,
                                categoryAppealTypeForCRT: categoryAppealType
                            },
                            documentName: '',
                            lineNumber: salesLine.data.salesLine.LineNumber!,
                            isSet: false,
                            documentType: azureBlobFileFormat,
                            propertyType: linePropertyType,
                            productName,
                            productID: salesLine.data.product?.RecordId?.toString(),
                            productType: prodType,
                            folioNumber: lineFolioNumber,
                        };
                        break;
                    case PropertyType.sdab:
                        const attributeValues = [...salesLine.data.salesLine.AttributeValues];

                        const appealAgainstType = attributeValues.find(line => line.Name === appealDevelopmentPermitType) //@ts-ignore
                            ?.TextValue! as string;
                        const [developmentPermit, subdivisionPermit, noticeOrder] = appealAgainstType.split('||');
                        const customerInformation: ICustomerInformation = {
                            //@ts-ignore
                            firstName: attributeValues.find(line => line.Name === customerFirstName)?.TextValue!,
                            //@ts-ignore
                            lastName: attributeValues.find(line => line.Name === customerLastName)?.TextValue!,
                            //@ts-ignore
                            email: attributeValues.find(line => line.Name === customerEmail)?.TextValue!,
                            productOwnerEmail: productOwnerEmailValue,
                            //@ts-ignore
                            emailConfirm: attributeValues.find(line => line.Name === customerEmailConfirm)?.TextValue!,
                            //@ts-ignore
                            address1: attributeValues.find(line => line.Name === customerAddress)?.TextValue!,
                            //@ts-ignore
                            address2: attributeValues.find(line => line.Name === customerAddress2)?.TextValue!,
                            //@ts-ignore
                            city: attributeValues.find(line => line.Name === customerCity)?.TextValue!,
                            //@ts-ignore
                            province: attributeValues.find(line => line.Name === customerProvince)?.TextValue!,
                            //@ts-ignore
                            postalCode: attributeValues.find(line => line.Name === customerPostalCode)?.TextValue!,
                            //@ts-ignore
                            residentialPhone: attributeValues.find(line => line.Name === customerResidentialPhone)?.TextValue!,
                            //@ts-ignore
                            country: attributeValues.find(line => line.Name === customerCountry)?.TextValue!,
                            //@ts-ignore
                            municipalAddress: attributeValues.find(line => line.Name === siteMunicipalAddressOfSite)?.TextValue!,
                            //@ts-ignore
                            otherDocuments: attributeValues.find(line => line.Name === siteOtherDocuments)?.TextValue!
                        };
                        const appelantInformation: IAppalentInformation = {
                            //@ts-ignore
                            firstName: attributeValues.find(line => line.Name === appelantFirstName)?.TextValue!,
                            //@ts-ignore
                            lastName: attributeValues.find(line => line.Name === appelantLastName)?.TextValue!,
                            //@ts-ignore
                            agentName: attributeValues.find(line => line.Name === appelantAgentName)?.TextValue!,
                            //@ts-ignore
                            email: attributeValues.find(line => line.Name === appelantEmailAddress)?.TextValue!,
                            productOwnerEmail: productOwnerEmailValue,
                            
                            //@ts-ignore
                            emailConfirm: attributeValues.find(line => line.Name === appelantEmailConfirmAddress)?.TextValue!,
                            // @ts-ignore
                            address1: attributeValues.find(line => line.Name === appelantAddress)?.TextValue!,
                            //@ts-ignore
                            address2: attributeValues.find(line => line.Name === appelantAddress2)?.TextValue!,
                            //@ts-ignore
                            city: attributeValues.find(line => line.Name === appelantCity)?.TextValue!,
                            //@ts-ignore
                            province: attributeValues.find(line => line.Name === appelantProvince)?.TextValue!,
                            //@ts-ignore
                            postalCode: attributeValues.find(line => line.Name === appelantPostalCode)?.TextValue!,
                            //@ts-ignore
                            residentialPhone: attributeValues.find(line => line.Name === appelantResidentialPhone)?.TextValue!
                        };
                        const appealAgainst: IAppealAgainst = {
                            appeal: '',
                            appealAgainstDevelopment: developmentPermit.trim(),
                            appealAgainstSubDivisionApplication: subdivisionPermit.trim(),
                            appealAgainstNoticOrder: noticeOrder.trim(),
                            //@ts-ignore
                            reasonOfAppeal: attributeValues.find(line => line.Name === appealReasonForAppeal)?.TextValue!,
                            //@ts-ignore
                            presentationTime: attributeValues.find(line => line.Name === appealEstimatedPresentationTime)?.TextValue!,
                            //@ts-ignore
                            usingAgentOrLegalCounsel: attributeValues.find(line => line.Name === appealUsingAgent)?.TextValue!
                        };
                        const anticipateIssues: IAnticapteIssues = {
                            isAnticipateIssuesWithYourAppeal:
                                //@ts-ignore
                                attributeValues.find(line => line.Name === appealAnticipateIssues)?.TextValue!,
                            isAnticipateIssuesWithYourAppealDescription:
                                //@ts-ignore
                                attributeValues.find(line => line.Name === appealReason)?.TextValue!
                        };
                        const witness: IBringingWitness = {
                            //@ts-ignore
                            bringingWitness: attributeValues.find(line => line.Name === appealBringingWitness)?.TextValue!,
                            //@ts-ignore
                            howManyWitness: attributeValues.find(line => line.Name === appealNumberOfWitness)?.TextValue!
                        };
                        lineDoc = {
                            sdab: {
                                anticipateIssues: anticipateIssues,
                                appealAgainst: appealAgainst,
                                appelantInformation: appelantInformation,
                                customerInformation: customerInformation,
                                witness: witness
                            },
                            documentName: '',
                            lineNumber: salesLine.data.salesLine.LineNumber!,
                            isSet: false,
                            documentType: azureBlobFileFormat,
                            propertyType: linePropertyType,
                            productName,
                            productID: salesLine.data.product?.RecordId?.toString(),
                            productType: prodType,
                            folioNumber: lineFolioNumber
                        };
                        break;
                    // below all cases have same implementation
                    case PropertyType.community:
                    case PropertyType.quadrant:
                    case PropertyType.blockProfile:
                    case PropertyType.cityWide:
                    case PropertyType.none:
                    case PropertyType.ats:
                        {
                            const productName = salesLine.data.product?.Name;
                            const doc = lineDocumentValue.split('||');
                            lineDoc = {
                                documentName: doc[1].trim(),
                                lineNumber: salesLine.data.salesLine.LineNumber!,
                                documentId: doc[0].trim(),
                                isSet: false,
                                propertyType: linePropertyType,
                                productType: prodType,
                                lookFor: lineValue,
                                productName: productName,
                                folioNumber: lineFolioNumber,
                                documentType: '',
                                rateId: rateId,
                                productID: salesLine.data.product?.RecordId?.toString()
                            };
                        }
                        break;
                    case PropertyType.cpid:
                    case PropertyType.rollNumber:
                    case PropertyType.lincNumber:
                    case PropertyType.titleNumber:
                        {
                            const productName = salesLine.data.product?.Name;
                            lineDoc = {
                                documentName: createDownloadDocName(createName(productName!, ' '), lineValue?.trim()),
                                lineNumber: salesLine.data.salesLine.LineNumber!,
                                isSet: false,
                                documentType: azureBlobFileFormat,
                                propertyType: linePropertyType,
                                productType: prodType,
                                lookFor: lineValue,
                                productName: productName,
                                folioNumber: lineFolioNumber,
                                rateId: rateId,
                                productID: salesLine.data.product?.RecordId?.toString()
                            };
                        }
                        break;
                    default:
                        {
                            const productName = salesLine.data.product?.Name;
                            lineDoc = {
                                documentName: '',
                                lineNumber: salesLine.data.salesLine.LineNumber!,
                                isSet: false,
                                documentType: '',
                                propertyType: PropertyType.default,
                                productType: '',
                                productName: productName,
                                folioNumber: lineFolioNumber,
                                rateId: rateId,
                                productID: salesLine.data.product?.RecordId?.toString()
                            };
                        }
                        break;
                }
                lineDocs.push({ ...lineDoc });
            }
        }
    }
    if (lineDocs.length > 0) {
        setLineDocument(prevState => [...prevState, ...lineDocs]);
    }
}

// function is to create a name for document
function createDocumentName(salesLine: ISalesLine, config: any) {
    let name: string = '';
    const { searchKeyNameAttribute, searchKeyValueAttribute, documentAttribute } = config;
    if (salesLine.data.salesLine.AttributeValues) {
        // Extracting Property type and value from line attributes
        const attributeName = salesLine.data.salesLine.AttributeValues.find(line => line.Name === searchKeyNameAttribute);
        const attributeValue = salesLine.data.salesLine.AttributeValues.find(line => line.Name === searchKeyValueAttribute);
        const attributeDocument = salesLine.data.salesLine.AttributeValues.find(line => line.Name === documentAttribute);
        // @ts-ignore
        const atributeNameText = attributeName?.TextValue?.trim();
        const linePropertyType = atributeNameText ? +atributeNameText : PropertyType.default;
        // @ts-ignore
        const lineValue = attributeValue?.TextValue! || '';
        // @ts-ignore
        const lineDocumentValue: string = attributeDocument?.TextValue! || '';

        // default is for Roll number, LINC, CPID and Title Number as it is only a string array
        switch (linePropertyType) {
            case PropertyType.address:
            case PropertyType.legalDescription:
            case PropertyType.legalDescriptionCondo:
                name = createName(createName(lineValue, '||'), ' ');
                break;
            case PropertyType.sdab:
            case PropertyType.lcsab:
                name = '';
                break;
            case PropertyType.community:
            case PropertyType.quadrant:
            case PropertyType.blockProfile:
            case PropertyType.ats:
            case PropertyType.cityWide:
            case PropertyType.none:
                name = lineDocumentValue.split('||')[1].trim();
                break;
            case PropertyType.cpid:
            case PropertyType.rollNumber:
            case PropertyType.lincNumber:
            case PropertyType.titleNumber:
                name = lineValue;
                break;
            default:
                name = '';
                break;
        }
    }
    return name;
}

// this function is to check and get zip file from azure blob
export function checkZipDocument(
    allDocuments: IAllDocuments,
    channelReferenceId: string,
    setAllDocuments: React.Dispatch<React.SetStateAction<IAllDocuments>>,
    context: ICoreContext<{ [x: string]: any }>,
    setBarWidth: React.Dispatch<React.SetStateAction<number>>,
    downloadableFiles: number
) {
    const { requestInterval } = context?.request.app.config;
    const interval = setInterval(async () => {
        if (!allDocuments.isAvailable && allDocuments.zipStatus) {
            try {
                const uniqueId = new Date().toString();
                const response = await getDocumentBlobSecureUrlAsync(
                    { callerContext: context.actionContext },
                    channelReferenceId,
                    '',
                    uniqueId
                );
                let bar = 1;
                const fileCount = response.FileCount;
                bar = ((fileCount === 0 ? 1 : fileCount) / downloadableFiles) * 100;
                if (response.Status && response.ZipStatus) {
                    allDocuments.isAvailable = true;
                    setAllDocuments(prevSt => ({
                        ...prevSt,
                        isAvailable: true,
                        zipStatus: true,
                        zipDocumentSecureURL: response.SecureBlobUrl
                    }));
                    // complete the progress bar
                    bar = 100;
                } else if (response.Status && !response.ZipStatus) {
                    allDocuments.isAvailable = false;
                    setAllDocuments(prevSt => ({
                        ...prevSt,
                        isAvailable: false,
                        zipStatus: false,
                        zipDocumentSecureURL: response.SecureBlobUrl,
                        isError: true
                    }));
                }
                setBarWidth(bar);
            } catch (error) {
                context.telemetry.error(`Failed to download the zip file ${error}`);
            }
        }
    }, requestInterval);
    return interval;
}
